// GlobalSearch — the app-wide search box in the top header. Lets a user jump to
// any item from one place: Deals, Deal Details, Farms, and Tasks. Results come
// back already grouped and ordered by type (Deals → Deal Details → Farms →
// Tasks) from GET /api/search, and are rendered under group headers in that
// order.
//
// THE BOX ALSO ASKS HARVI. The first row is always "Ask Harvi: <what you
// typed>" and it is the default, so Enter asks; arrow down to open a record
// instead. Asking dispatches `harvi-ask`, which ChatWidget answers by opening
// and sending the question in a new private thread. `/` focuses the box from
// anywhere that is not already a text field.
//
// Selecting a result calls `onNavigate(result)` (supplied by App.jsx), which
// deep-links to the item's page. Fully keyboard-driven: type to search, ↑/↓ to
// move, Enter to open, Esc to close.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { B, radius, font, weight } from "../../constants/brand";
import { api } from "../../api/client";
import { Icon } from "../ui";

const DEBOUNCE_MS = 220;
const MIN_TERM_LEN = 2;

// Display order + labels + glyphs per result type. The backend already returns
// results in this order; this drives the group headers and row icons.
const TYPE_META = {
  deal:       { label: "Deals",         icon: "deals" },
  dealDetail: { label: "Deal Details",  icon: "deals" },
  farm:       { label: "Farms",         icon: "farms" },
  task:       { label: "Tasks",         icon: "tasks" },
  note:       { label: "Notes",         icon: "note" },
};
const TYPE_ORDER = ["deal", "dealDetail", "farm", "task", "note"];

const isTypingTarget = (el) => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

export function GlobalSearch({ onNavigate }) {
  const [term, setTerm]       = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive]   = useState(0);   // highlighted row index (flat)
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const reqSeq   = useRef(0);                   // guards against out-of-order responses

  // Debounced fetch.
  useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_TERM_LEN) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const seq = ++reqSeq.current;
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`search?q=${encodeURIComponent(q)}`);
        if (seq !== reqSeq.current) return;      // a newer request superseded this one
        setResults(res.results || []);
      } catch {
        if (seq === reqSeq.current) setResults([]);
      } finally {
        if (seq === reqSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // `/` from anywhere focuses the box.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ask = useCallback((text) => {
    const q = String(text || "").trim();
    if (!q) return;
    setOpen(false);
    setTerm("");
    setResults([]);
    inputRef.current?.blur();
    window.dispatchEvent(new CustomEvent("harvi-ask", { detail: { text: q } }));
  }, []);

  const choose = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setTerm("");
    setResults([]);
    inputRef.current?.blur();
    onNavigate?.(item);
  }, [onNavigate]);

  // Row 0 is "Ask Harvi"; rows 1..n are the search results.
  const rowCount = results.length + 1;
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); return; }
    if (e.key === "ArrowDown")      { e.preventDefault(); setActive((a) => Math.min(a + 1, rowCount - 1)); setOpen(true); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter")     {
      e.preventDefault();
      if (active === 0 || !results.length) ask(term); else choose(results[active - 1]);
    }
  }, [results, active, rowCount, term, ask, choose]);

  // Group results by type for the section headers while preserving the flat
  // index (so keyboard highlight lines up with the visible rows).
  const groups = useMemo(() => {
    let flat = 0;
    return TYPE_ORDER
      .map((type) => {
        const rows = results
          .filter((r) => r.type === type)
          .map((r) => ({ ...r, _idx: flat++ }));
        return { type, rows: rows.map((r) => ({ ...r, _idx: r._idx + 1 })) };
      })
      .filter((g) => g.rows.length > 0);
  }, [results]);

  const showDropdown = open && term.trim().length >= 1;
  const searching = term.trim().length >= MIN_TERM_LEN;

  const rowStyle = (activeRow) => ({
    display: "flex", gap: 10, width: "100%", textAlign: "left", padding: "10px 14px",
    border: "none", borderBottom: `1px solid ${B.border}`,
    background: activeRow ? B.tealLt : B.surface, cursor: "pointer", fontFamily: "inherit", alignItems: "center",
  });
  const ellipsis = { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: "1 1 auto", maxWidth: 560, minWidth: 0 }}>
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: "flex", alignItems: "center", gap: 10, height: 38, boxSizing: "border-box",
          padding: "0 6px 0 6px", background: B.surface, border: `1px solid ${B.border}`,
          borderRadius: 12, boxShadow: B.shadowXs, cursor: "text",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
        onFocusCapture={(e) => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.boxShadow = B.ringAccent; }}
        onBlurCapture={(e) => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.boxShadow = B.shadowXs; }}
      >
        <span aria-hidden="true" style={{
          width: 26, height: 26, borderRadius: "50%", background: B.gradBrand, color: B.white, flexShrink: 0,
          display: "grid", placeItems: "center", fontSize: 12, fontWeight: weight.bold,
        }}>H</span>
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Ask Harvi, or search deals, notes, documents…"
          aria-label="Ask Harvi or search"
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
            fontSize: font.base, fontFamily: "inherit", color: B.text, padding: 0,
          }}
        />
        {term ? (
          <button
            onClick={(e) => { e.stopPropagation(); setTerm(""); setResults([]); inputRef.current?.focus(); }}
            title="Clear"
            aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", color: B.textLt, display: "flex", padding: 4 }}
          >
            <Icon name="close" size={14} />
          </button>
        ) : (
          <kbd aria-hidden="true" title="Press / to focus" style={{
            fontFamily: "inherit", fontSize: 11, color: B.textMd, background: B.surfaceMuted,
            border: `1px solid ${B.border}`, borderBottomWidth: 2, borderRadius: 6, padding: "1px 7px",
          }}>/</kbd>
        )}
      </div>

      {showDropdown ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            width: "100%",
            minWidth: 360,
            maxWidth: "90vw",
            maxHeight: 460,
            display: "flex",
            flexDirection: "column",
            background: B.surface,
            border: `1px solid ${B.divider}`,
            borderRadius: radius.xxl,
            boxShadow: B.shadowMd,
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowY: "auto" }}>
            <button onClick={() => ask(term)} onMouseEnter={() => setActive(0)} style={rowStyle(active === 0)}>
              <span style={{ width: 20, display: "flex", justifyContent: "center", flexShrink: 0, color: B.navy }}>
                <Icon name="message" size={16} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ ...ellipsis, color: B.text, fontSize: font.base, fontWeight: weight.semibold }}>
                  Ask Harvi: “{term.trim()}”
                </span>
                <span style={{ ...ellipsis, color: B.textMd, fontSize: font.sm, marginTop: 1 }}>
                  Opens a new private chat
                </span>
              </span>
              {active === 0 ? <kbd style={{ fontFamily: "inherit", fontSize: 11, color: B.textMd }}>Enter</kbd> : null}
            </button>
            {!searching ? null : loading && results.length === 0 ? (
              <div style={{ padding: 18, textAlign: "center", color: B.textLt, fontSize: font.base }}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={{ padding: 18, textAlign: "center", color: B.textLt, fontSize: font.base }}>
                No records match “{term.trim()}”
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.type}>
                  <div
                    style={{
                      padding: "8px 14px 4px",
                      fontSize: 10,
                      fontWeight: weight.bold,
                      letterSpacing: 1.1,
                      textTransform: "uppercase",
                      color: B.textLt,
                      background: B.surfaceMuted,
                    }}
                  >
                    {TYPE_META[g.type].label}
                  </div>
                  {g.rows.map((r) => (
                    <button
                      key={`${r.type}-${r.id}`}
                      onClick={() => choose(r)}
                      onMouseEnter={() => setActive(r._idx)}
                      style={rowStyle(r._idx === active)}
                    >
                      <span style={{ width: 20, display: "flex", justifyContent: "center", flexShrink: 0, color: B.navy }}>
                        <Icon name={TYPE_META[r.type].icon} size={16} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ ...ellipsis, color: B.text, fontSize: font.base, fontWeight: weight.semibold }}>
                          {r.title || "(untitled)"}
                        </span>
                        {r.subtitle ? (
                          <span style={{ ...ellipsis, color: B.textMd, fontSize: font.sm, marginTop: 1 }}>
                            {r.subtitle}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
